import React, { lazy, Suspense, useState, useEffect, useRef, useMemo } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, Sun, Moon } from "lucide-react";
import { globalLogout } from "../utils/globalLogout";
import { useAuth } from "../context/AuthContext";
import { auth } from "../firebaseConfig";
import SiteFooter from "../components/SiteFooter";
import { publicPageMeta } from "../config/publicPageMeta";
import SEOHead from "../components/SEOHead";
import PremiumSaleBanner from "../components/sale/PremiumSaleBanner";

// Native LiveChatWidget retired — chat is now handled by the Havlo widget.
const TheScopeAI = lazy(() => import("../components/admin/TheScopeAI"));

/* -------------------- DROPDOWN -------------------- */
function NavDropdown({ title, items, theme }) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const onEsc = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("keydown", onEsc);
    return () => document.removeEventListener("keydown", onEsc);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location]);

  return (
    <div className="relative" ref={wrapperRef}>
      <button
        onClick={() => setOpen((v) => !v)}
        className={`font-semibold transition-colors ${theme === "dark" ? "text-white hover:text-blue-300" : "text-slate-700 hover:text-blue-600"}`}
      >
        {title}
      </button>

      {open && (
        <div className="absolute left-0 mt-2 bg-white rounded-md shadow-lg p-4 space-y-2 z-50 min-w-max border border-gray-100">
          {items.map(({ label, to }) => (
            <Link
              key={to}
              to={to}
              className="block text-sm text-gray-700 hover:text-blue-700 whitespace-nowrap"
              onClick={() => setOpen(false)}
            >
              {label}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

/* -------------------- LAYOUT -------------------- */
export default function HomePageLayout() {
  const { currentUser, client } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const [theme, setTheme] = useState(
    localStorage.getItem("scope180-theme") || "light"
  );
  const location = useLocation();
  const navigate = useNavigate();

  const isLoggedIn = !!currentUser;
  const role = client?.role || "";

  /* -------------------- THEME ENGINE -------------------- */
  useEffect(() => {
    const root = document.documentElement;
    if (theme === "dark") {
      root.classList.add("dark");
      root.style.backgroundColor = "#0a1628"; // Blue ombre base
    } else {
      root.classList.remove("dark");
      root.style.backgroundColor = "#ffffff";
    }
    localStorage.setItem("scope180-theme", theme);
    window.dispatchEvent(new Event("themechange"));
  }, [theme]);

  const getDashboardPath = (role) => {
    switch ((role || "").toLowerCase()) {
      case "admin":
      case "director":
        return "/admin/dashboard";
      case "mentor":
        return "/mentor/dashboard";
      case "developer":
        return "/learner/dashboard";
      case "support":
        return "/learner/dashboard";
      default:
        return "/learner/dashboard";
    }
  };

  const dashboardPath = getDashboardPath(role);

  useEffect(() => {
    setMenuOpen(false);
  }, [location]);

  /* -------------------- PAGE TITLE -------------------- */
  const meta = useMemo(() => {
    const path = location.pathname;
    // Exact match first
    if (publicPageMeta[path]) return publicPageMeta[path];
    // Prefix match for dynamic routes like /blog/:slug, /products/:slug
    const matchedKey = Object.keys(publicPageMeta).find(
      (key) => key !== "/" && path.startsWith(key + "/")
    );
    if (matchedKey) return publicPageMeta[matchedKey];
    // Fallback: generate title from path
    const segments = path.split("/").filter(Boolean);
    const lastSegment = segments[segments.length - 1] || "Home";
    return {
      title: lastSegment.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
    };
  }, [location.pathname]);

  // Title is now set via <SEOHead> in the render below

  /* -------------------- LOGOUT -------------------- */
  const handleLogout = async () => {
    try {
      await globalLogout(auth, "/");
    } catch (err) {
      console.error("Logout failed:", err);
    } finally {
      setMenuOpen(false);
      navigate("/", { replace: true });
    }
  };

  /* -------------------- NAV ITEMS -------------------- */
  const learningResourcesItems = [
    { label: "TheScope180 Academy", to: "/academy" },
    { label: "PM Certification Pathfinder", to: "/pm-certification-pathfinder" },
    { label: "Deliverables Suite", to: "/deliverables" },
    { label: "Skill Builders", to: "/skill-builders" },
    { label: "Practice Exams", to: "/products?tab=exams" },
  ];

  return (
    <div className={`flex flex-col min-h-screen transition-colors duration-500 ${
      theme === "dark"
        ? "bg-gradient-to-b from-[#0a1628] via-[#0f2447] to-[#0a1628] text-white"
        : "bg-gray-50 text-gray-900"
    }`}>
      {/* ================= SEO HEAD ================= */}
      <SEOHead
        title={meta.title}
        description={meta.description}
        ogImage={meta.ogImage}
        path={location.pathname}
      />

      {/* ================= HEADER ================= */}
      <header className={`shadow-md sticky top-0 z-50 transition-colors duration-500 ${
        theme === "dark"
          ? "bg-gradient-to-r from-[#0a1628] via-[#132d5e] to-[#0a1628] border-b border-blue-900/50"
          : "bg-white border-b border-slate-200"
      }`}>
        <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
          <Link to="/" className="text-2xl font-extrabold tracking-tighter">
            <span className={theme === "dark" ? "text-white" : "text-slate-900"}>THE</span>
            <span className={theme === "dark" ? "text-yellow-400" : "text-yellow-600"}>SCOPE180</span>
          </Link>

          {/* -------- Desktop Nav -------- */}
          <nav className={`hidden md:flex gap-6 items-center ${theme === "dark" ? "text-white" : "text-slate-700"}`}>
            <NavDropdown title="Learning Resources" items={[
              { label: "All Products", to: "/products" },
              ...learningResourcesItems
            ]} theme={theme} />

            <Link to="/blog" className={`font-semibold transition-colors ${theme === "dark" ? "hover:text-blue-300" : "hover:text-blue-600"}`}>
              Blog
            </Link>

            <Link to="/contact" className={`font-semibold transition-colors ${theme === "dark" ? "hover:text-blue-300" : "hover:text-blue-600"}`}>
              Contact
            </Link>

            <Link to="/aboutme" className={`font-semibold transition-colors ${theme === "dark" ? "hover:text-blue-300" : "hover:text-blue-600"}`}>
              About
            </Link>

            {/* Theme Toggle */}
            <button
              onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
              className={`p-2 rounded-lg border transition-all group ${
                theme === "dark"
                  ? "border-white/20 hover:bg-white/10"
                  : "border-slate-200 hover:bg-slate-100"
              }`}
              title={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
            >
              {theme === "dark" ? (
                <Sun size={18} className="text-yellow-400 group-hover:rotate-45 transition-transform" />
              ) : (
                <Moon size={18} className="text-slate-500 group-hover:-rotate-12 transition-transform" />
              )}
            </button>

            {!isLoggedIn ? (
              <Link
                to={`/login?redirect=${encodeURIComponent(location.pathname)}`}
                className={`px-4 py-2 rounded-lg font-bold transition-all shadow-sm ${
                  theme === "dark"
                    ? "bg-white text-blue-800 hover:bg-blue-50"
                    : "bg-blue-700 text-white hover:bg-blue-600"
                }`}
              >
                Register / Login
              </Link>
            ) : (
              <div className="flex items-center gap-2">
                <Link
                  to={dashboardPath}
                  className="px-4 py-2 rounded-lg bg-yellow-400 text-black font-bold hover:bg-yellow-300 transition-all shadow-sm"
                >
                  Dashboard
                </Link>
                <button
                  onClick={handleLogout}
                  className={`px-4 py-2 rounded-lg border font-semibold transition-all ${
                    theme === "dark"
                      ? "border-white text-white hover:bg-white/10"
                      : "border-slate-300 text-slate-600 hover:bg-slate-100"
                  }`}
                >
                  Logout
                </button>
              </div>
            )}
          </nav>

          {/* -------- Mobile Toggle -------- */}
          <div className="md:hidden flex items-center gap-3">
            <button
              onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
              className={`p-2 rounded-lg border transition-all ${
                theme === "dark" ? "border-white/20 hover:bg-white/10" : "border-slate-200 hover:bg-slate-100"
              }`}
            >
              {theme === "dark" ? (
                <Sun size={20} className="text-yellow-400" />
              ) : (
                <Moon size={20} className="text-slate-500" />
              )}
            </button>
            <button className={theme === "dark" ? "text-white" : "text-slate-700"} onClick={() => setMenuOpen((v) => !v)}>
              {menuOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>
        </div>

        {/* -------- Mobile Menu -------- */}
        {menuOpen && (
          <div className={`md:hidden px-6 py-6 space-y-6 border-t transition-colors duration-500 ${
            theme === "dark"
              ? "bg-gradient-to-b from-[#132d5e] to-[#0a1628] text-white border-blue-900/50"
              : "bg-white text-slate-700 border-slate-200"
          }`}>
            <div>
              <p className={`font-bold uppercase text-xs tracking-widest mb-2 ${
                theme === "dark" ? "text-yellow-400" : "text-yellow-600"
              }`}>Learning Resources</p>
              <div className="pl-4 space-y-3">
                <Link to="/products" className={`block font-semibold ${theme === "dark" ? "text-gray-200" : "text-slate-600"}`}>
                  All Products
                </Link>
                {learningResourcesItems.map((i) => (
                  <Link key={i.to} to={i.to} className={`block ${theme === "dark" ? "text-gray-200" : "text-slate-600"}`}>
                    {i.label}
                  </Link>
                ))}
              </div>
            </div>

            <Link to="/blog" className="block font-semibold">Blog</Link>
            <Link to="/contact" className="block font-semibold">Contact</Link>
            <Link to="/aboutme" className="block font-semibold">About</Link>

            <div className={`pt-4 border-t ${
              theme === "dark" ? "border-slate-700" : "border-slate-200"
            }`}>
              {!isLoggedIn ? (
                <Link to="/login" className="block w-full text-center bg-yellow-400 text-black py-3 rounded-xl font-bold">
                  Register / Login
                </Link>
              ) : (
                <div className="space-y-3">
                  <Link to={dashboardPath} className="block w-full text-center bg-yellow-400 text-black py-3 rounded-xl font-bold">
                    Go to Dashboard
                  </Link>
                  <button onClick={handleLogout} className={`block w-full text-center border py-3 rounded-xl ${
                    theme === "dark" ? "border-white/30 text-white" : "border-slate-300 text-slate-600"
                  }`}>
                    Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </header>

      {/* Black Friday Premium sale bar (sale window only; client-side, not in SEO shells) */}
      <PremiumSaleBanner variant="site" />

      {/* ================= CONTENT ================= */}
      <main className="flex-grow">
        <Outlet />
      </main>

      {/* ================= FOOTER ================= */}
      <SiteFooter />

      {/* Havlo widget handles all chat via index.html embed */}

    </div>
  );
}